import React, { useEffect, useMemo, useState } from 'react';
import { Modal, TextField, MenuItem, FormControl, InputAdornment } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import dayjs from 'dayjs';
import moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';
import CalendarIcon from '../../../CalendarIcon';
import { FormInputText } from '../../../../form-components/FormInputText';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import { errorMessages } from '../../../../utils/methods';
import { bulkStopMemberPasses } from '../../../../api/member-management';
import { getAllStaffs } from '../../../../api/staff';
import {
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  FormSection,
  ButtonWrapper,
  CancelButton,
  SaveButton,
  StyledSelect,
} from '../../ChangeTFreezyModalStyled';
import {
  ModalContainer,
  TopFormGrid,
  TopFormLeftCol,
  DateRow,
  TopFormField,
  TopFieldLabel,
  FilterTabsRow,
  FilterChip,
  SelectAllRow,
  SelectAllLeftGroup,
  SelectAllLabel,
  ToggleGroup,
  ToggleLabel,
  TealSwitch,
  CardsGrid,
  ListEmptyState,
  ProductCard,
  ItemCheckbox,
  ItemInfo,
  ItemNameRow,
  ItemTypeLabel,
  ItemName,
  ItemMetaRow,
  ItemMetaText,
  ItemStopInfoText,
  SummaryBar,
  SummaryText,
  SummaryHighlight,
} from './BulkStopModalStyled';

// 필터 탭 정의. value는 백엔드 targetType과 동일한 값을 사용한다.
const FILTER_TABS = [
  { value: 'ALL', label: '전체' },
  { value: 'TICKET', label: '수강권' },
  { value: 'PASS', label: '이용권' },
  { value: 'LOCKER', label: '락커' },
  { value: 'PRODUCT', label: '일반상품' },
];

// 회원권/일반상품 4종을 targetType별 공통 필드로 정규화한다.
// - 이미 중지된(STOPPED 등 ACTIVE 아님) 항목은 재중지 대상이 아니므로 목록에서 제외한다.
//   (사용예정은 status가 ACTIVE라 그대로 노출·선택 가능. 만료는 애초에 active 목록에 없음.)
// - passId: 백엔드 bulk-stop API로 전송할 값. 라커는 반드시 stopTargetPassId(그룹 대표가 만료건일 수 있어).
// - disabled: 라커인데 stopTargetPassId가 없어 중지 대상을 특정할 수 없는 경우.
const buildItems = (userTickets, generalProducts) => {
  const tickets = (userTickets?.activeTickets ?? [])
    .filter(item => item.status === 'ACTIVE')
    .map(item => ({
    uid: `TICKET_${item.memberCourseTicketId}`,
    targetType: 'TICKET',
    passId: item.memberCourseTicketId,
    name: item.ticketName,
    typeLabel: '수강권',
    startDate: item.issueDate,
    expireDate: item.expireDate,
    status: item.status,
    stopCount: item.stopCount,
    stopPeriod: item.stopPeriod,
    remainingCount: Math.max((Number(item.usageCount) || 0) - (Number(item.usedCount) || 0), 0),
    disabled: item.status !== 'ACTIVE',
  }));

  const passes = (userTickets?.activePasses ?? [])
    .filter(item => item.status === 'ACTIVE')
    .map(item => ({
    uid: `PASS_${item.memberCoursePassId}`,
    targetType: 'PASS',
    passId: item.memberCoursePassId,
    name: item.ticketName,
    typeLabel: '이용권',
    startDate: item.issueDate,
    expireDate: item.expireDate,
    status: item.status,
    stopCount: item.stopCount,
    stopPeriod: item.stopPeriod,
    remainingCount: null,
    disabled: item.status !== 'ACTIVE',
  }));

  // 한 자리에 이용권이 여러 개면 중지 대상(stopTargetPassId)이 같다.
  // 그대로 두면 목록에 같은 대상이 중복으로 뜨고, 둘 다 선택하면 두 번째 중지가 406 이라 일괄중지 전체가 롤백된다.
  const seenLockerStopTargets = new Set();
  const lockers = (generalProducts?.lockerPasses ?? [])
    .filter(item => {
      const expired = item.status !== 'ACTIVE'
        || (item.expireDate && dayjs(item.expireDate).isBefore(dayjs().startOf('day')));
      if (expired || !item.stopTargetPassId) {
        return false;
      }
      if (seenLockerStopTargets.has(item.stopTargetPassId)) {
        return false;
      }
      seenLockerStopTargets.add(item.stopTargetPassId);
      return true;
    })
    .map(item => ({
    uid: `LOCKER_${item.memberLockerPassId}`,
    targetType: 'LOCKER',
    passId: item.stopTargetPassId,
    name: item.productName,
    typeLabel: '락커',
    startDate: item.startDate,
    expireDate: item.expireDate,
    status: item.status,
    stopCount: item.stopCount,
    stopPeriod: item.stopPeriod,
    remainingCount: null,
    disabled: false,
  }));

  const products = (generalProducts?.productPasses ?? [])
    .filter(item => item.status === 'ACTIVE')
    .map(item => ({
    uid: `PRODUCT_${item.memberProductPassId}`,
    targetType: 'PRODUCT',
    passId: item.memberProductPassId,
    name: item.productName,
    typeLabel: '일반상품',
    startDate: item.startDate,
    expireDate: item.expireDate,
    status: item.status,
    stopCount: item.stopCount,
    stopPeriod: item.stopPeriod,
    remainingCount: null,
    disabled: item.status !== 'ACTIVE',
  }));

  return [...tickets, ...passes, ...lockers, ...products];
};

// 기준 날짜 + 중지일 수 → 종료 날짜(양끝 포함). 기준 날짜가 없거나 일수가 1 미만이면 계산 불가(null).
const endFromDays = (start, days) => {
  const numDays = Number(days);
  if (!start || !Number.isInteger(numDays) || numDays < 1) return null;
  return moment(start).add(numDays - 1, 'day').toDate();
};

// 기준 날짜 ~ 종료 날짜(양끝 포함) → 중지일 수. 종료가 시작보다 빠르면 계산 불가(null).
const daysFromEnd = (start, end) => {
  if (!start || !end) return null;
  const diff = moment(end).startOf('day').diff(moment(start).startOf('day'), 'day');
  return diff >= 0 ? diff + 1 : null;
};

// 보유 수강권/이용권/라커/일반상품을 체크박스로 여러 개 선택해 동일 기간으로 한 번에 중지하는 모달.
// 일일권(activeTrialTickets)은 중지 미지원이라 대상에서 제외한다.
export default function BulkStopModal({ open, onClose, userTickets, generalProducts, memberId, bigUser, getAllUserTickets }) {
  const { t } = useTranslation();
  const toast = useToast();
  const branchId = bigUser?.branch?.branchId;
  const headBranchId = bigUser?.headBranch?.headBranchId;

  const [filterTab, setFilterTab] = useState('ALL');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [ignoreStopLimit, setIgnoreStopLimit] = useState(false);
  const [stopDays, setStopDays] = useState('');

  const { data: staffData } = useQuery({
    queryKey: ['staffList', headBranchId, branchId],
    queryFn: () => getAllStaffs(bigUser.token, `headBranchId=${headBranchId}&branchId=${branchId}&page=1&limit=100`),
    enabled: !!bigUser?.token && !!branchId && open,
  });
  const staffList = staffData?.content ?? [];

  const { handleSubmit, control, watch, setValue, reset } = useForm({
    defaultValues: { startDate: null, endDate: null, managerId: '', memo: '' },
  });

  const watchedStartDate = watch('startDate');
  const watchedEndDate = watch('endDate');
  const watchedManagerId = watch('managerId');

  // 모달을 열 때마다 이전 선택/입력을 초기화한다. 기준 날짜는 오늘로 기본 세팅.
  useEffect(() => {
    if (open) {
      setFilterTab('ALL');
      setSelectedIds(new Set());
      setIgnoreStopLimit(false);
      setStopDays('');
      reset({ startDate: new Date(), endDate: null, managerId: '', memo: '' });
    }
  }, [open, reset]);

  // 기준 날짜 ~ 중지 종료 날짜(양끝 포함) 중지 일수. 종료가 시작보다 빠르면 무효(null).
  const stopDaysCount = useMemo(() => {
    if (!watchedStartDate || !watchedEndDate) return null;
    const diff = moment(watchedEndDate).startOf('day').diff(moment(watchedStartDate).startOf('day'), 'day');
    return diff >= 0 ? diff + 1 : null;
  }, [watchedStartDate, watchedEndDate]);
  const isRangeValid = stopDaysCount != null && stopDaysCount >= 1;

  // 기준 날짜 / 종료 날짜 / 중지일 세 입력을 서로 동기화한다.
  // FormInputDate의 onChange는 사용자가 직접 달력을 조작할 때만 호출되므로(setValue로는 재호출되지 않음)
  // 아래 핸들러끼리 서로를 다시 트리거하는 무한루프는 발생하지 않는다.
  const handleStartDateChange = date => {
    const daysNum = Number(stopDays);
    const isStopDaysValid = stopDays !== '' && Number.isInteger(daysNum) && daysNum >= 1;
    if (isStopDaysValid) {
      setValue('endDate', endFromDays(date, daysNum));
    } else if (watchedEndDate) {
      const computedDays = daysFromEnd(date, watchedEndDate);
      setStopDays(computedDays != null ? String(computedDays) : '');
    }
  };

  const handleEndDateChange = date => {
    const computedDays = daysFromEnd(watchedStartDate, date);
    setStopDays(computedDays != null ? String(computedDays) : '');
  };

  const handleStopDaysChange = e => {
    const raw = e.target.value;
    setStopDays(raw);
    const daysNum = Number(raw);
    if (watchedStartDate && raw !== '' && Number.isInteger(daysNum) && daysNum >= 1) {
      setValue('endDate', endFromDays(watchedStartDate, daysNum));
    }
  };

  const allItems = useMemo(() => buildItems(userTickets, generalProducts), [userTickets, generalProducts]);
  const displayedItems = useMemo(
    () => (filterTab === 'ALL' ? allItems : allItems.filter(item => item.targetType === filterTab)),
    [allItems, filterTab]
  );
  const selectableDisplayedItems = displayedItems.filter(item => !item.disabled);
  const isAllDisplayedSelected =
    selectableDisplayedItems.length > 0 && selectableDisplayedItems.every(item => selectedIds.has(item.uid));

  const toggleItem = item => {
    if (item.disabled) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(item.uid)) {
        next.delete(item.uid);
      } else {
        next.add(item.uid);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (isAllDisplayedSelected) {
        selectableDisplayedItems.forEach(item => next.delete(item.uid));
      } else {
        selectableDisplayedItems.forEach(item => next.add(item.uid));
      }
      return next;
    });
  };

  const handleClose = () => {
    onClose();
  };

  const formatDateText = value => (value ? moment(value).format('YYYY-MM-DD') : 'YYYY-MM-DD');

  const stopInfoText = item => {
    // 강제중지(중지 제한 해제)로 잔여가 음수가 될 수 있어 표시만 0으로 하한 처리(도메인 값은 음수 유지).
    const countText = item.stopCount === null || item.stopCount === undefined ? '무제한' : `${Math.max(0, item.stopCount)}회`;
    const periodText = item.stopPeriod === null || item.stopPeriod === undefined ? '무제한' : `${Math.max(0, item.stopPeriod)}일`;
    return `중지 가능 일수: ${periodText} / 중지 가능 횟수: ${countText}`;
  };

  const onSubmit = async values => {
    const { startDate, endDate, managerId, memo } = values;

    if (selectedIds.size === 0) {
      toast.error('중지할 상품을 선택해주세요');
      return;
    }
    if (!startDate) {
      toast.error('기준 날짜를 선택해주세요');
      return;
    }
    if (!endDate) {
      toast.error('중지 종료 날짜를 선택해주세요');
      return;
    }
    if (!isRangeValid) {
      toast.error('중지 종료 날짜가 기준 날짜보다 빠릅니다');
      return;
    }

    const items = allItems
      .filter(item => selectedIds.has(item.uid))
      .map(item => ({ targetType: item.targetType, passId: item.passId }));

    try {
      const data = {
        items,
        stopPeriod: stopDaysCount,
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        managerId: managerId || null,
        memo: memo || null,
        ignoreStopLimit,
      };
      await bulkStopMemberPasses(bigUser.token, memberId, data);
      toast.success(t('Successfully saved'));
      await getAllUserTickets();
      handleClose();
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const isSubmitDisabled = selectedIds.size === 0 || !watchedStartDate || !watchedEndDate || !isRangeValid;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="bulk-stop-modal-title"
      aria-describedby="bulk-stop-modal-description">
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>일괄중지</ModalTitle>
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalContent sx={{ overflowY: 'auto' }}>
          <FormSection>
            {/* 기준 날짜 / 중지일 / 관리 담당자 / 메모 */}
            <TopFormGrid>
              <TopFormLeftCol>
                <DateRow>
                  <TopFormField sx={{ flex: 1, minWidth: 0 }}>
                    <TopFieldLabel>기준 날짜</TopFieldLabel>
                    <FormInputDate
                      name="startDate"
                      control={control}
                      placeholder="YYYY-MM-DD"
                      onChange={handleStartDateChange}
                      components={{ OpenPickerIcon: CalendarIcon }}
                      renderInput={params => (
                        <TextField
                          sx={{
                            '& .MuiOutlinedInput-root': { height: '44px', borderRadius: '8px' },
                            '& .MuiSvgIcon-root': { fill: '#0F7F85 !important' },
                          }}
                          size="small"
                          {...params}
                        />
                      )}
                    />
                  </TopFormField>

                  <TopFormField sx={{ flex: 1, minWidth: 0 }}>
                    <TopFieldLabel>종료 날짜</TopFieldLabel>
                    <FormInputDate
                      name="endDate"
                      control={control}
                      placeholder="YYYY-MM-DD"
                      minDate={watchedStartDate || null}
                      disabled={!watchedStartDate}
                      onChange={handleEndDateChange}
                      components={{ OpenPickerIcon: CalendarIcon }}
                      renderInput={params => (
                        <TextField
                          sx={{
                            '& .MuiOutlinedInput-root': { height: '44px', borderRadius: '8px' },
                            '& .MuiSvgIcon-root': { fill: '#0F7F85 !important' },
                          }}
                          size="small"
                          {...params}
                        />
                      )}
                    />
                  </TopFormField>
                </DateRow>

                <TopFormField>
                  <TopFieldLabel>중지일</TopFieldLabel>
                  <TextField
                    type="number"
                    size="small"
                    value={stopDays}
                    onChange={handleStopDaysChange}
                    placeholder="0"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">일</InputAdornment>,
                      inputProps: { min: 1 },
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { height: '44px', borderRadius: '8px' } }}
                  />
                </TopFormField>

                <TopFormField>
                  <TopFieldLabel>관리 담당자</TopFieldLabel>
                  <FormControl size="small" fullWidth>
                    <StyledSelect value={watchedManagerId} onChange={e => setValue('managerId', e.target.value)} displayEmpty>
                      <MenuItem value="">
                        <em>선택</em>
                      </MenuItem>
                      {staffList.map(staff => (
                        <MenuItem key={staff.id} value={staff.id}>
                          {staff.name}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </FormControl>
                </TopFormField>
              </TopFormLeftCol>

              <TopFormField sx={{ height: '100%' }}>
                <TopFieldLabel>메모</TopFieldLabel>
                <FormInputText
                  name="memo"
                  control={control}
                  placeholder="메모를 입력해 주세요"
                  multiline
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    '& .MuiOutlinedInput-root': {
                      height: '100%',
                      alignItems: 'stretch',
                      borderRadius: '8px',
                      padding: '10px 14px',
                    },
                    '& .MuiOutlinedInput-input': { height: '100% !important', overflow: 'auto !important' },
                  }}
                />
              </TopFormField>
            </TopFormGrid>

            {/* 상품 타입 필터 */}
            <FilterTabsRow>
              {FILTER_TABS.map(tabItem => (
                <FilterChip
                  key={tabItem.value}
                  isActive={filterTab === tabItem.value}
                  onClick={() => setFilterTab(tabItem.value)}>
                  {tabItem.label}
                </FilterChip>
              ))}
            </FilterTabsRow>

            {/* 전체선택 + 중지 제한 해제 토글 */}
            <SelectAllRow>
              <SelectAllLeftGroup>
                <ItemCheckbox
                  checked={isAllDisplayedSelected}
                  indeterminate={!isAllDisplayedSelected && selectableDisplayedItems.some(item => selectedIds.has(item.uid))}
                  disabled={selectableDisplayedItems.length === 0}
                  onChange={toggleSelectAll}
                />
                <SelectAllLabel onClick={toggleSelectAll}>전체선택</SelectAllLabel>
              </SelectAllLeftGroup>

              <ToggleGroup>
                <TealSwitch
                  checked={ignoreStopLimit}
                  onChange={e => setIgnoreStopLimit(e.target.checked)}
                  inputProps={{ 'aria-label': '중지 제한 해제' }}
                />
                <ToggleLabel>중지 제한 해제</ToggleLabel>
              </ToggleGroup>
            </SelectAllRow>

            {/* 상품 카드 그리드 */}
            <CardsGrid>
              {displayedItems.length === 0 ? (
                <ListEmptyState>선택 가능한 상품이 없습니다.</ListEmptyState>
              ) : (
                displayedItems.map(item => {
                  const daysRemaining = item.expireDate
                    ? dayjs(item.expireDate).startOf('day').diff(dayjs().startOf('day'), 'day')
                    : null;
                  return (
                    <ProductCard key={item.uid} disabled={item.disabled} onClick={() => toggleItem(item)}>
                      <ItemCheckbox
                        checked={selectedIds.has(item.uid)}
                        disabled={item.disabled}
                        onClick={e => e.stopPropagation()}
                        onChange={() => toggleItem(item)}
                      />
                      <ItemInfo>
                        <ItemNameRow>
                          <ItemTypeLabel>{item.typeLabel}</ItemTypeLabel>
                          <ItemName>{item.name}</ItemName>
                        </ItemNameRow>
                        <ItemMetaRow>
                          <ItemMetaText>
                            유효기간 {item.startDate?.replaceAll('-', '.') || '-'} ~{' '}
                            {item.expireDate?.replaceAll('-', '.') || '-'}
                          </ItemMetaText>
                          {daysRemaining !== null && daysRemaining >= 0 && (
                            <ItemMetaText>잔여일수 {daysRemaining}일</ItemMetaText>
                          )}
                          {item.remainingCount !== null && <ItemMetaText>잔여횟수 {item.remainingCount}회</ItemMetaText>}
                        </ItemMetaRow>
                        <ItemStopInfoText>{stopInfoText(item)}</ItemStopInfoText>
                      </ItemInfo>
                    </ProductCard>
                  );
                })
              )}
            </CardsGrid>

            {/* 요약 */}
            <SummaryBar>
              {selectedIds.size === 0 || !isRangeValid ? (
                <SummaryText>중지할 상품과 기간을 선택해 주세요</SummaryText>
              ) : (
                <SummaryText>
                  선택된 상품 <SummaryHighlight>{selectedIds.size}개</SummaryHighlight> · {formatDateText(watchedStartDate)}{' '}
                  ~ {formatDateText(watchedEndDate)} · <SummaryHighlight>{stopDaysCount}일간 중지</SummaryHighlight>
                </SummaryText>
              )}
            </SummaryBar>
          </FormSection>

          <ButtonWrapper>
            <CancelButton onClick={handleClose}>취소</CancelButton>
            <SaveButton isActive={!isSubmitDisabled} disabled={isSubmitDisabled} onClick={handleSubmit(onSubmit)}>
              일괄중지
            </SaveButton>
          </ButtonWrapper>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
}
